import { useEffect, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";

import { Checkbox } from "../../../components/ui/checkbox";
import { Switch } from "../../../components/ui/switch";
import { DIAS, DIAS_UTEIS, MAX_PERIODOS, ORDEM, SIGLAS, duracao, faixas, normalizarHora, opcoesDeHora, paraMin, pausaEntre, periodoInvalido, resumoDoDia, sugestaoDePeriodo } from "../lib/horarios";

/**
 * Campo de hora com lista (dono, 25/10/2026): ao clicar abre os horários de 30 em
 * 30 minutos que fazem sentido — o "fecha" só depois da hora de abrir, o "abre"
 * do 2º/3º horário só depois do fechamento anterior. Digitar continua valendo
 * ("1830" vira 18:30 ao sair; hora impossível volta ao que era) e filtra a lista.
 */
function CampoHora({ valor, onMudar, invalido, rotulo, e2e, opcoes = [] }) {
  const [texto, setTexto] = useState(valor);
  const [piscar, setPiscar] = useState(false);
  const [lista, setLista] = useState(false);
  const caixa = useRef(null);
  useEffect(() => setTexto(valor), [valor]);
  // A lista abre com o horário atual (ou o primeiro possível) no meio
  useEffect(() => {
    if (!lista || !caixa.current) return;
    const alvo = caixa.current.querySelector("[data-atual=true]");
    if (alvo) caixa.current.scrollTop = alvo.offsetTop - caixa.current.clientHeight / 2 + alvo.clientHeight / 2;
  }, [lista]);
  const confirmar = () => {
    setLista(false);
    const v = normalizarHora(texto);
    if (!v) { setTexto(valor); setPiscar(true); setTimeout(() => setPiscar(false), 900); return; }
    setTexto(v);
    if (v !== valor) onMudar(v);
  };
  const escolher = (t) => { setTexto(t); setLista(false); if (t !== valor) onMudar(t); };
  const digitando = texto !== valor && texto.length > 0;
  const visiveis = digitando ? opcoes.filter((t) => t.startsWith(texto)) : opcoes;
  return (
    <div className="relative">
      <input type="text" inputMode="numeric" maxLength={5} placeholder="00:00" autoComplete="off" aria-label={rotulo} data-e2e={e2e}
        value={texto} aria-expanded={lista}
        onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 4); setTexto(d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d); setLista(true); }}
        onFocus={(e) => { setLista(true); setTimeout(() => e.target.select(), 0); }}
        onClick={() => setLista(true)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === "Escape") setLista(false);
        }}
        className={`w-[72px] rounded-lg border bg-muted/50 px-2 py-1.5 text-center text-sm tabular-nums tracking-wide outline-none focus:bg-background focus:border-primary ${invalido || piscar ? "border-red-500 focus:border-red-500" : "border-border"}`} />
      {lista && visiveis.length > 0 && (
        <ul ref={caixa} role="listbox" aria-label={rotulo} data-e2e={`lista-${e2e}`}
          className="absolute left-0 top-full z-30 mt-1 max-h-56 w-[88px] overflow-y-auto rounded-lg border bg-popover py-1 shadow-lg">
          {visiveis.map((t) => (
            <li key={t}>
              <button type="button" role="option" aria-selected={t === valor} data-atual={t === valor || undefined}
                onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(t)}
                className={`w-full px-3 py-1 text-left text-sm tabular-nums hover:bg-muted ${t === valor ? "bg-primary/10 font-semibold text-primary" : ""}`}>
                {t}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Copiar o horário deste dia para outros dias. */
function CopiarPara({ dw, onCopiar, onCancelar }) {
  const [sel, setSel] = useState(() => new Set());
  const outros = ORDEM.filter((x) => x !== dw);
  const todos = outros.every((x) => sel.has(x));
  const alternar = (x) => setSel((s) => { const n = new Set(s); n.has(x) ? n.delete(x) : n.add(x); return n; });
  const chip = (ativo) => `rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${ativo ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:bg-muted"}`;
  return (
    <div className="mt-2.5 rounded-xl bg-muted/60 p-2.5 space-y-2" data-e2e="copiar-para">
      <p className="text-xs text-muted-foreground">Copiar o horário de {DIAS[dw].toLowerCase()} para:</p>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" className={chip(todos)} onClick={() => setSel(todos ? new Set() : new Set(outros))}>Todos</button>
        <button type="button" className={chip(false)} onClick={() => setSel(new Set(DIAS_UTEIS.filter((x) => x !== dw)))}>Seg a Sex</button>
        {outros.map((x) => (
          <button key={x} type="button" className={chip(sel.has(x))} aria-pressed={sel.has(x)} onClick={() => alternar(x)} data-e2e={`copiar-${SIGLAS[x]}`}>{SIGLAS[x]}</button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-3">
        <button type="button" className="text-xs font-semibold text-muted-foreground hover:underline" onClick={onCancelar}>Cancelar</button>
        <button type="button" disabled={!sel.size} onClick={() => onCopiar([...sel])} data-e2e="copiar-aplicar"
          className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:bg-muted disabled:text-muted-foreground">
          Copiar{sel.size ? ` para ${sel.size} ${sel.size > 1 ? "dias" : "dia"}` : ""}
        </button>
      </div>
    </div>
  );
}

/**
 * Um dia da semana. Fechado: mostra o resumo ("11:00 às 15:00 e 18:00 às 23:00"),
 * clicar abre a edição. Editando: aberto/fechado, 24 horas, até 3 períodos com
 * a pausa entre eles, e copiar para outros dias.
 */
export default function CartaoDia({ dia, editando, onEditar, onConcluir, mudar, copiando, onCopiar, onAbrirCopia, onFecharCopia }) {
  const aberto = faixas(dia).length > 0;
  const fechadoCls = !aberto ? "border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30" : "border-border bg-card";

  if (!editando) {
    return (
      <div role="button" tabIndex={0} onClick={onEditar} data-e2e={`dia-${SIGLAS[dia.dw]}`} aria-label={`Editar ${DIAS[dia.dw]}`}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEditar(); } }}
        className={`group rounded-xl border px-3 py-2.5 cursor-pointer transition-colors hover:border-muted-foreground/40 ${fechadoCls}`}>
        <div className="flex items-center gap-2 min-h-[24px]">
          <span className={`flex-1 font-semibold text-[15px] ${aberto ? "" : "text-red-700 dark:text-red-400"}`}>{DIAS[dia.dw]}</span>
          <span className={`text-[11px] font-semibold px-2 py-px rounded-full border group-hover:hidden group-focus-within:hidden ${aberto ? "border-primary text-primary" : "border-red-600 bg-red-600 text-white dark:border-red-500 dark:bg-red-500"}`}>
            {aberto ? "Aberta" : "Fechada"}
          </span>
          <Pencil className={`w-4 h-4 hidden group-hover:block group-focus-within:block ${aberto ? "text-muted-foreground" : "text-red-600"}`} />
        </div>
        <p className={`text-[13.5px] mt-0.5 ${aberto ? "text-muted-foreground" : "font-semibold text-red-700 dark:text-red-400"}`}>{resumoDoDia(dia)}</p>
      </div>
    );
  }

  const ordem = dia.periodos.map((p, i) => [p, i]).sort((a, b) => paraMin(a[0][0]) - paraMin(b[0][0]));
  const trocar = (i, k, v) => mudar((d) => { d.periodos[i][k] = v === "00:00" && k === 1 ? "23:59" : v; return d; });
  const remover = (i) => mudar((d) => { d.periodos.splice(i, 1); if (!d.periodos.length) d.aberto = false; return d; });
  const algumInvalido = dia.aberto && !dia.h24 && dia.periodos.some(periodoInvalido);

  return (
    <div className={`rounded-xl border px-3 py-2.5 ${aberto ? "border-muted-foreground/40 bg-card" : "border-red-400 bg-red-50 dark:border-red-800 dark:bg-red-950/30"}`} data-e2e={`editando-${SIGLAS[dia.dw]}`}>
      <div className="flex items-center gap-2 min-h-[24px]">
        <span className={`flex-1 font-semibold text-[15px] ${aberto ? "" : "text-red-700 dark:text-red-400"}`}>{DIAS[dia.dw]}</span>
        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer">
          {dia.aberto ? "Aberta" : "Fechada"}
          <Switch checked={dia.aberto} data-e2e="dia-aberto"
            onCheckedChange={(v) => mudar((d) => { d.aberto = v; if (v && !d.periodos.length) d.periodos.push(["11:00", "15:00"]); return d; })} />
        </label>
      </div>

      {dia.aberto && (
        <label className={`mt-2.5 flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[13px] font-medium cursor-pointer ${dia.h24 ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
          <Checkbox checked={dia.h24} onCheckedChange={(v) => mudar((d) => { d.h24 = v === true; return d; })} data-e2e="dia-24h" />
          Aberta 24 horas
        </label>
      )}

      {dia.aberto && !dia.h24 && (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {ordem.map(([p, i], k) => {
            const pausa = k > 0 ? pausaEntre(ordem[k - 1][0], p) : 0;
            return (
              <div key={i}>
                {pausa > 0 && <p className="pl-1 pb-1.5 text-xs text-muted-foreground/80">Pausa de {duracao(pausa)}</p>}
                <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground" data-e2e="periodo">
                  <CampoHora valor={p[0]} onMudar={(v) => trocar(i, 0, v)} rotulo="Abre" e2e="abre"
                    opcoes={opcoesDeHora({ tipo: "abre", aPartirDe: k > 0 ? ordem[k - 1][0][1] : null })} />
                  às
                  <CampoHora valor={p[1]} onMudar={(v) => trocar(i, 1, v)} invalido={periodoInvalido(p)} rotulo="Fecha" e2e="fecha"
                    opcoes={opcoesDeHora({ tipo: "fecha", depoisDe: p[0] })} />
                  <button type="button" onClick={() => remover(i)} aria-label="Remover horário" data-e2e="remover-periodo"
                    className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground/70 hover:text-red-600 hover:bg-muted">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {algumInvalido && (
            <p className="text-xs text-red-600 dark:text-red-400 leading-snug" data-e2e="aviso-meia-noite">
              O horário de fechar precisa ser depois do de abrir. Passa da meia-noite? Feche às 23:59 e abra o dia seguinte às 00:00.
            </p>
          )}
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-between">
        {dia.aberto && !dia.h24 && dia.periodos.length < MAX_PERIODOS
          ? <button type="button" className="text-[13px] font-semibold text-primary hover:underline" data-e2e="adicionar-periodo"
              onClick={() => mudar((d) => { d.periodos.push(sugestaoDePeriodo(d)); return d; })}>+ Adicionar horário</button>
          : <span />}
        <button type="button" className="text-[13px] font-semibold text-muted-foreground hover:underline" onClick={onConcluir} data-e2e="concluir-dia">Concluir</button>
      </div>

      {copiando
        ? <CopiarPara dw={dia.dw} onCopiar={onCopiar} onCancelar={onFecharCopia} />
        : <div className="mt-1.5"><button type="button" className="text-[13px] font-semibold text-muted-foreground hover:underline" onClick={onAbrirCopia} data-e2e="abrir-copiar">Copiar para outros dias</button></div>}
    </div>
  );
}
